import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import { Film, ExternalLink, Plus, Calendar } from "lucide-react";

export default function FilmTheatreSubmit() {
  const { user } = useAuth();
  const [active, setActive] = useState([]);
  const [show, setShow] = useState(false);

  useEffect(() => {
    api.get("/film/submissions").then((r) => setActive(r.data || []));
  }, []);

  return (
    <div className="my-16" data-testid="film-theatre-block">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="label text-pfc-gold mb-2 flex items-center gap-2"><Film size={14} /> PFC Film Theatre</div>
          <h2 className="font-display text-3xl lg:text-4xl text-white uppercase">Now Playing</h2>
          <p className="text-white/60 mt-2 text-sm max-w-xl">Independent filmmakers list their films here for $29.99/month. Premium placement, audience reach, and magazine cross-promotion.</p>
        </div>
        <button
          onClick={() => user ? setShow(true) : (window.location.href = "/login?next=/film")}
          data-testid="submit-film-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-xs hover:bg-pfc-gold-bright"
        >
          <Plus size={14} /> Submit Your Film · $29.99/mo
        </button>
      </div>

      {active.length === 0 ? (
        <div className="py-10 border border-dashed border-white/10 text-center text-white/40 text-sm">No active film submissions yet. Be the first.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="film-theatre-grid">
          {active.map((f) => (
            <div key={f.submission_id} className="pfc-card overflow-hidden" data-testid={`film-sub-${f.submission_id}`}>
              {f.youtube_id ? (
                <YouTubeEmbed videoId={f.youtube_id} title={f.title} />
              ) : (
                <div className="aspect-video bg-white/5 grid place-items-center text-white/30">
                  {f.poster_url ? <img src={f.poster_url} alt={f.title} className="w-full h-full object-cover" /> : <Film size={48} />}
                </div>
              )}
              <div className="p-5">
                <h3 className="font-display text-xl text-white">{f.title}</h3>
                {f.director && <div className="text-pfc-gold text-[11px] tracking-[0.3em] uppercase font-sub mt-1">Dir. {f.director}</div>}
                <p className="text-white/70 text-sm mt-3 line-clamp-3">{f.synopsis}</p>
                <div className="flex items-center justify-between mt-4">
                  {f.expires_at && <div className="text-white/40 text-[11px] inline-flex items-center gap-1"><Calendar size={11} /> Until {new Date(f.expires_at).toLocaleDateString()}</div>}
                  <a href={f.youtube_url} target="_blank" rel="noreferrer" className="text-pfc-gold text-[11px] inline-flex items-center gap-1 hover:underline"><ExternalLink size={11} /> Watch</a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {show && <FilmSubmitModal user={user} onClose={() => setShow(false)} />}
    </div>
  );
}

function FilmSubmitModal({ user, onClose }) {
  const [form, setForm] = useState({
    title: "", director: "", synopsis: "", poster_url: "", youtube_url: "", runtime_minutes: "",
    contact_email: user?.email || "",
  });
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        runtime_minutes: form.runtime_minutes ? parseInt(form.runtime_minutes, 10) : null,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/film/submissions/checkout", payload);
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Submission failed");
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="film-submit-modal">
      <div className="bg-ink-900 border border-pfc-gold/40 max-w-lg w-full p-7">
        <div className="flex items-baseline justify-between mb-5">
          <h3 className="font-display text-2xl">Submit Your Film</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white text-sm" data-testid="film-submit-close">Close</button>
        </div>
        <p className="text-white/60 text-sm mb-5">$29.99/month to display on the Film Theatre page. Auto-renews monthly. Cancel anytime in your dashboard.</p>
        <form onSubmit={submit} className="space-y-3">
          <I label="Film Title" required value={form.title} onChange={(v) => upd("title", v)} testId="fs-title" />
          <I label="Director" value={form.director} onChange={(v) => upd("director", v)} testId="fs-director" />
          <T label="Synopsis" required value={form.synopsis} onChange={(v) => upd("synopsis", v)} testId="fs-synopsis" />
          <I label="YouTube / Vimeo URL" required value={form.youtube_url} onChange={(v) => upd("youtube_url", v)} placeholder="https://youtu.be/..." testId="fs-url" />
          <I label="Poster Image URL (optional)" value={form.poster_url} onChange={(v) => upd("poster_url", v)} testId="fs-poster" />
          <div className="grid grid-cols-2 gap-3">
            <I label="Runtime (min)" type="number" min="0" value={form.runtime_minutes} onChange={(v) => upd("runtime_minutes", v)} testId="fs-runtime" />
            <I label="Contact Email" required type="email" value={form.contact_email} onChange={(v) => upd("contact_email", v)} testId="fs-email" />
          </div>
          <button type="submit" disabled={loading} data-testid="fs-pay-btn" className="w-full py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright disabled:opacity-50">
            {loading ? "Redirecting…" : "Pay $29.99 & List My Film →"}
          </button>
        </form>
      </div>
    </div>
  );
}

function I({ label, value, onChange, required, type = "text", placeholder, min, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">{label}{required && <span className="text-pfc-red"> *</span>}</div>
      <input type={type} min={min} required={required} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none" />
    </label>
  );
}
function T({ label, value, onChange, required, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">{label}{required && <span className="text-pfc-red"> *</span>}</div>
      <textarea required={required} value={value} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testId} className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-white focus:border-pfc-gold focus:outline-none resize-y" />
    </label>
  );
}
